import React, { ReactNode } from "react";
import classNames from "classnames/bind";
import FAQ from "common/blocks/FAQ";
import MediaPlayer from "common/blocks/MediaPlayer";
import ArticleCarFeatures from "common/blocks/RichText/ArticleCarFeatures";
import FeaturedFigures from "common/blocks/RichText/FeaturedFigures";
import Gallery from "common/blocks/RichText/Gallery";
import ReviewsContainer from "common/blocks/RichText/ReviewsContainer";
import ReviewsPageContent from "common/blocks/RichText/ReviewsPageContent";
import Sponsorship from "common/blocks/RichText/Sponsorship";
import Timeline from "common/blocks/RichText/Timeline";
import SmartLink from "common/wrapper/SmartLink";
import FeaturedCard from "features/news/components/FeaturedSection/FeaturedCard";
import type { ResolvedStaticPageSubscribe } from "resolvers/data/staticPageSubscribeResolver";
import StaticPageSubscribe from "common/blocks/Subscribe";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import type { HTMLReactParserOptions } from "html-react-parser";
import parse, { Element, domToReact } from "html-react-parser";
import type { ResolvedItem, ResolvedRichText } from "resolvers/data/richTextResolver";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";
import IconUl from "../IconUl";
import InlineFrame from "../InlineFrame";
import Testimonials from "../Testimonials";
import TextMediaBlock from "../TextMediaBlock";
import EventSection from "../TextMediaBlock/EventSection";
import AddedProtection from "./AddedProtection";
import Table from "./Table";
import styles from "./RichText.module.scss";
import AccordionGroup, { AccordionGroupProps } from "../AccordionGroup";
import Quotation from "./Quotation";
import Shopfront from "./Shopfront";
import CookieSettingsWidget from "../CookieSettingsWidget";
import PageAnchor from "./PageAnchor";
import SignUpBlock from "../SignUpBlock";
import StaticCardList from "../StaticCardList";
import RelatedArticles from "../RelatedArticles";
import { CMSImage } from "../Image/cms-image";
import StaticPageComponentWrapper from "../StaticPageComponentWrapper";
import { MediaContentTable } from "../MediaContentTable";
import { CopyOnly } from "./CopyOnly";

const cx = classNames.bind(styles);

const getImage = (props: any, passedProps: { imageType?: "article" }) => {
    const { alt, captionText, link, linkTarget, ...rest } = props;

    const ImageWrapper = link
        ? ({ children }: { children: React.ReactNode }) => (
              <SmartLink href={link} prefetch={false}>
                  <a target={linkTarget}>{children}</a>
              </SmartLink>
          )
        : React.Fragment;

    if (passedProps?.imageType === "article")
        return (
            <ImageWrapper>
                <CMSImage
                    className={cx("image")}
                    alt={alt}
                    {...rest}
                    style={{ objectFit: "cover" }}
                    width={640}
                    height={380}
                />
            </ImageWrapper>
        );

    return (
        <ImageWrapper>
            <CMSImage className={cx("image")} alt={alt} {...rest} />
        </ImageWrapper>
    );
};

const getComponent = (
    node: Element,
    resolvedItems: ResolvedItem[],
    passedProps: any,
    isAlternateTheme: boolean,
    isStaticPage: boolean
) => {
    const codename = node.attribs["data-codename"];
    const matchingCodename = (item: ResolvedItem) => item.codename === codename;
    const props = resolvedItems.find(matchingCodename);

    if (!props) return <></>;

    switch (props.type) {
        case ContentTypeCodenames.Image: {
            const { captionText } = props;
            return captionText ? (
                <div className={cx("imageWrapper", "globalImageWrapper")}>
                    {getImage(props, passedProps)}
                    <div className={cx("captionText")}>{captionText}</div>
                </div>
            ) : (
                <>{getImage(props, passedProps)}</>
            );
        }
        case ContentTypeCodenames.SignUpBlock:
            return <SignUpBlock {...props} />;

        case ContentTypeCodenames.SectionFeaturedFigures:
            return <FeaturedFigures {...props} />;

        case ContentTypeCodenames.SectionIconList:
            return <ArticleCarFeatures {...props} />;

        case ContentTypeCodenames.Video:
            return (
                <div className={cx("mediaPlayerContainer")}>
                    <MediaPlayer {...props} isBorderRounded={isStaticPage} />
                </div>
            );

        case ContentTypeCodenames.CookieSettingsWidget:
            return (
                <div className={cx("widgetContainer")}>
                    <CookieSettingsWidget {...props} />
                </div>
            );

        case ContentTypeCodenames.SectionImageGallery:
            return <Gallery {...props} />;

        case ContentTypeCodenames.ReviewsContent:
            return (
                <div className={cx("reviewsContentWrapper")}>
                    <ReviewsPageContent {...props} />
                </div>
            );
        case ContentTypeCodenames.AboutUsTimeline:
            return <Timeline {...props} />;

        case ContentTypeCodenames.SponsorshipCard:
            return <Sponsorship {...props} />;

        case ContentTypeCodenames.ReviewsService:
            return <ReviewsContainer reviews={passedProps} />;

        case ContentTypeCodenames.Feature:
            if (!props.link) return;
            return (
                <FeaturedCard
                    image={props.link.image}
                    title={props.link.title}
                    ctaLabel={props.ctaLabel}
                    isInline
                    isSmall={props.isSmall}
                    href={props.link.path}
                />
            );

        case ContentTypeCodenames.FAQ:
            return (
                <div className={cx("faqContentWrapper")}>
                    <FAQ
                        {...props}
                        isAlternateTheme={isAlternateTheme}
                        isStaticPage={isStaticPage}
                        {...passedProps.faq}
                        isStructuredData={true}
                    />
                </div>
            );

        case ContentTypeCodenames.InlineFrame:
            return <InlineFrame {...props} />;

        case ContentTypeCodenames.IconList:
            return <IconUl listHtml={props.html} />;

        case ContentTypeCodenames.TextMediaBlock:
            return (
                <div className={cx("textMediaContainer")}>
                    <TextMediaBlock {...props} />
                </div>
            );
        case ContentTypeCodenames.HeroTextImage:
            return (
                <div className={cx("textMediaContainer")}>
                    <TextMediaBlock {...props} isHero />
                </div>
            );
        case ContentTypeCodenames.EventBlock:
            const Event = <EventSection {...props} />;
            return (
                <div className={cx("textMediaContainer")}>
                    <TextMediaBlock {...props} content={Event} isEvent />
                </div>
            );
        case ContentTypeCodenames.Testimonials:
            return <Testimonials {...props} />;

        case ContentTypeCodenames.StaticPageSubscribe: {
            const { signupForm, image } = props as ResolvedStaticPageSubscribe;

            return <StaticPageSubscribe image={image} signupForm={signupForm} />;
        }

        case ContentTypeCodenames.AddedProtectionSection:
            return <AddedProtection {...props} />;

        case ContentTypeCodenames.CustomTable:
            return <Table {...props} />;

        case ContentTypeCodenames.AccordionGroup:
            return <AccordionGroup {...(props as AccordionGroupProps)} />;

        case ContentTypeCodenames.Quotation:
            return <Quotation {...props} />;

        case ContentTypeCodenames.Shopfront:
            return <Shopfront {...props} {...passedProps} />;

        case ContentTypeCodenames.PageAnchor:
            return <PageAnchor {...props} />;

        case ContentTypeCodenames.StaticCardList:
            return <StaticCardList {...props} />;

        case ContentTypeCodenames.RelatedArticles:
            return <RelatedArticles {...props} />;

        case ContentTypeCodenames.DynamicPhoneNumber:
            return <DynamicPhoneNumber {...props} />;

        case ContentTypeCodenames.StaticPageComponentWrapper:
            return <StaticPageComponentWrapper {...props} />;

        case ContentTypeCodenames.MediaContentTable:
            return <MediaContentTable {...props} />;

        case ContentTypeCodenames.CopyOnly:
            return <CopyOnly {...props} />;

        default:
            return <></>;
    }
};

interface RichTextProps extends ResolvedRichText {
    className?: string;
    props?: any;
    isAlternateTheme?: boolean;
    isStaticPage?: boolean;
}

const RichText = (props: RichTextProps) => {
    const options: HTMLReactParserOptions = {
        replace: (node) => {
            const isComponentNode = node instanceof Element && !!node.attribs?.["data-codename"];

            if (!props.resolvedItems || !isComponentNode) {
                const nodeEl = node as Element;

                if (nodeEl.name === "img") {
                    return <>{getImage(nodeEl.attribs, props.props)}</>;
                }
                if (nodeEl.name === "a") {
                    try {
                        const parsedUrl = new URL(nodeEl.attribs.href);
                        if (parsedUrl.hostname !== "www.motorpoint.co.uk") {
                            nodeEl.attribs.rel = "nofollow";
                        }
                        return node;
                    } catch {
                        return node;
                    }
                }

                if (nodeEl.name === "table") {
                    return (
                        <div className={cx("hscroll-table")}>
                            <table>{domToReact(nodeEl.children)}</table>
                        </div>
                    );
                }

                return node;
            } else {
                return (
                    <div className={cx("componentWrap")}>
                        <>
                            {getComponent(
                                node,
                                props.resolvedItems,
                                props.props,
                                !!props.isAlternateTheme,
                                !!props.isStaticPage
                            )}
                        </>
                    </div>
                );
            }
        },
    };

    const parsedHtml = parse(props.html, options);

    return (
        <div className={cx("richText", props.className, { isStaticPage: props.isStaticPage })}>
            {parsedHtml}
        </div>
    );
};

export default RichText;
